package com.klantenstop.web.pages.profile

import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.models.dto.requests.user.UpdatePassword
import com.klantenstop.web.data.models.dto.requests.user.UpdateUser
import com.klantenstop.web.data.repos.UserRepo
import com.materialkobweb.utils.ViewModel
import com.materialkobweb.utils.Logger
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val userRepo: UserRepo = UserRepo
) : ViewModel(), ProfileContract {
    private val _state = MutableStateFlow(ProfileContract.State())
    override val state = _state

    private val _effect = MutableSharedFlow<ProfileContract.Effect>()
    override val effect = _effect

    init {
        sendIntent(ProfileContract.Intent.LoadInitialData)
    }

    override fun sendIntent(intent: ProfileContract.Intent) = coroutineScope.launch {
        when (intent) {
            is ProfileContract.Intent.LoadInitialData -> loadInitialData()
            is ProfileContract.Intent.UpdateFirstName -> updateState { it.copy(firstName = intent.firstName) }
            is ProfileContract.Intent.UpdateLastName -> updateState { it.copy(lastName = intent.lastName) }
            is ProfileContract.Intent.UpdateEmail -> updateState { it.copy(email = intent.email) }
            is ProfileContract.Intent.Update -> updateCurrentUser()
            is ProfileContract.Intent.ToggleChangingPassword -> updateState { it.copy(isChangingPassword = !it.isChangingPassword) }
            is ProfileContract.Intent.UpdatePassword -> updateCurrentUserPassword()
            is ProfileContract.Intent.ChangeConfirmNewPassword -> _state.update { it.copy(confirmNewPassword = intent.confirmNewPassword) }
            is ProfileContract.Intent.ChangeNewPassword -> _state.update { it.copy(newPassword = intent.newPassword) }
            is ProfileContract.Intent.ChangeOldPassword -> _state.update { it.copy(oldPassword = intent.oldPassword) }
            is ProfileContract.Intent.ResendVerificationEmail -> resendVerificationEmail()
        }
    }

    private suspend fun loadInitialData() {
        try {
            val currentUser = userRepo.getCurrentUser()
            updateState {
                it.copy(
                    currentUser = currentUser,
                    firstName = currentUser.firstName,
                    lastName = currentUser.lastName,
                    email = currentUser.email ?: ""
                )
            }
        } catch (e: Exception) {
            // If we can't load the user, they might not be logged in
            // The PageLayout will handle redirecting to login if needed
            Logger.error("ProfileViewModel", "Failed to load user profile:", e)
        }
    }

    private suspend fun updateCurrentUser() {
        val state = _state.value
        val updateUser = UpdateUser(firstName = state.firstName, lastName = state.lastName, email = state.email)

        try {
            val result = userRepo.updateCurrentUser(updateUser)
            updateState { it.copy(currentUser = result, firstName = result.firstName, email = result.email) }
            ToastManager.success("Profiel succesvol bijgewerkt", title = "Gelukt")
        } catch (e: Exception) {
            ToastManager.error(e.message ?: "Er is een fout opgetreden bij het bijwerken van je profiel", title = "Fout")
        }
    }

    private suspend fun updateCurrentUserPassword() {
        val state = _state.value
        val updatePassword = UpdatePassword(
            state.oldPassword,
            state.newPassword,
            state.confirmNewPassword
        )

        try {
            userRepo.updatePasswordCurrentUser(updatePassword)
            updateState { it.copy(isChangingPassword = false, oldPassword = "", newPassword = "", confirmNewPassword = "") }
            ToastManager.success("Wachtwoord succesvol gewijzigd", title = "Gelukt")
        } catch (e: Exception) {
            ToastManager.error(e.message ?: "Er is een fout opgetreden bij het wijzigen van je wachtwoord", title = "Fout")
        }
    }

    private suspend fun resendVerificationEmail() {
        if (_state.value.resendCooldownSeconds > 0) {
            ToastManager.info("Wacht nog ${_state.value.resendCooldownSeconds} seconden voordat je opnieuw kunt verzenden", title = "Te snel")
            return
        }

        try {
            userRepo.resendVerificationEmail()
            // resendVerificationEmail() only returns SuccessfullySent on success, throws on error
            updateState { it.copy(verificationEmailSent = true, resendCooldownSeconds = 120) }
            startCooldownTimer()
            ToastManager.success("Verificatie-e-mail is verzonden! Controleer je inbox.", title = "E-mail verzonden")
        } catch (e: Exception) {
            ToastManager.error(e.message ?: "Er is een fout opgetreden bij het verzenden van de verificatie-e-mail", title = "Fout")
        }
    }

    private fun startCooldownTimer() = coroutineScope.launch {
        while (_state.value.resendCooldownSeconds > 0) {
            delay(1000)
            updateState { it.copy(resendCooldownSeconds = it.resendCooldownSeconds - 1) }
        }
    }

    override fun emitEffect(effect: ProfileContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (ProfileContract.State) -> ProfileContract.State) {
        _state.update(block)
    }
}
